import { Treatment } from 'libs/end-to-end/src/treatment';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { BiasAnalysisOutput } from 'libs/end-to-end/src/bias-analysis';
import { RootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { getStringValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { inferTypeForFilterStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { BiasAnalysisRequest } from 'libs/end-to-end/src/custom-viz/BiasAnalysisTypes';
import { UseDimensionConfigPayload } from 'libs/end-to-end/src/use-dimension-config-payload';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { DeepDiveNumberOfSubjectComponent } from 'libs/end-to-end/src/deep-dive-number-of-subject-component';

export const getDeepDiveSectionSelectedTreatment = ({
  selectedTopTreatmentIndex,
  topTreatments,
}: {
  topTreatments: Treatment[];
  selectedTopTreatmentIndex: Nullable<number>;
}) => {
  if (selectedTopTreatmentIndex !== null) {
    return [topTreatments[selectedTopTreatmentIndex]];
  }
  return [];
};

/**
 * Given a dimension level, find the dimension that contains that level.
 */
export const findDimensionForLevel = ({
  allDimensions,
  level,
}: {
  allDimensions: DimensionDefinition[];
  level: string | null;
}) => {
  if (!level) {
    return null;
  }

  for (const dimension of allDimensions) {
    const levels = dimension.levels.map((level) => level.columnValue);

    if (levels.includes(level)) {
      return dimension;
    }
  }

  return null;
};

export const isDataVisualizationEqualToFocusAreaAndGroups = ({
  focusArea,
  treatmentGroup,
  referenceGroup,
  graph,
}: {
  focusArea: FocusArea;
  treatmentGroup: string;
  referenceGroup: string;
  graph: RootCauseGroupResult;
}) => {
  return (
    graph.visualizationAttributes &&
    graph.visualizationAttributes.focusArea === focusArea &&
    graph.visualizationAttributes.treatmentLevel === treatmentGroup &&
    graph.visualizationAttributes.referenceLevel === referenceGroup
  );
};

export const getFormattedDeepDiveSectionData = ({
  data,
  treatmentGroup,
  referenceGroup,
  metric: metric,
  dimensionDefinition,
  focusArea,
  allDimensions,
}: {
  data: Nullable<BiasAnalysisOutput>;
  allDimensions: DimensionDefinition[];
  focusArea: FocusArea;
  dimensionDefinition: Nullable<DimensionDefinition>;
  treatmentGroup: Nullable<string>;
  referenceGroup: Nullable<string>;
  metric: Nullable<string>;
}) => {
  if (!treatmentGroup || !referenceGroup) {
    return null;
  }

  if (!data) {
    return null;
  }

  const newData = data.disparities.find(
    (disparity) =>
      disparity.demographicReference === referenceGroup &&
      disparity.demographicComparison === treatmentGroup,
  );

  const explanatoryFactors = data.insights
    .filter((insight) => {
      return (
        insight.metric === metric &&
        insight.dimension === dimensionDefinition?.columnHeaderValue &&
        insight.referenceLevel === referenceGroup &&
        insight.treatmentLevel === treatmentGroup
      );
    })
    .flatMap((insight) => insight.groupResults);

  const dimension = findDimensionForLevel({
    allDimensions,
    level: treatmentGroup,
  });

  if (!dimension) {
    return null;
  }

  if (!newData) {
    return null;
  }

  if (newData.rawDisparity === null) {
    return null;
  }

  if (newData.controlledDisparity === null) {
    return null;
  }

  if (newData.controlledPvalue === null) {
    return null;
  }

  const dataVisualizations = data.visualizations.filter((visualization) => {
    return isDataVisualizationEqualToFocusAreaAndGroups({
      focusArea,
      treatmentGroup: treatmentGroup,
      referenceGroup: referenceGroup,
      graph: visualization,
    });
  });

  return {
    rawDisparity: newData.rawDisparity,
    controlledDisparity: newData.controlledDisparity,
    debiasDisparity: newData.debiasDisparity,
    certainty: 100 - newData.controlledPvalue,
    treatmentGroupCount: newData.treatmentLevelCount,
    treatmentGroupNumerator: newData.treatmentNumerator,
    referenceGroupCount: newData.referenceLevelCount,
    referenceGroupNumerator: newData.referenceNumerator,
    treatmentLevel: newData.demographicComparison,
    referenceLevel: newData.demographicReference,
    dimension: dimension.columnHeaderValue,
    visualizations: dataVisualizations,
    explanatoryFactors: explanatoryFactors,
  };
};

/**
 * If you are trying to figure out why the infer logic is here, see the below references.
 * @see EmployeeGroupMenu
 * @see determineDefaultValueForPrimaryFilter
 * @param primaryFilterValues
 */
export const getTreatmentGroup = (
  primaryFilterValues: PrimaryFilterSelection[],
) => {
  const employeeGroupLevel = primaryFilterValues.find(
    (filter) => filter.primaryFilterKey === 'employeeGroup',
  );

  if (!employeeGroupLevel) {
    return null;
  }

  const infer = inferTypeForFilterStore(employeeGroupLevel.value);

  if (infer.type !== 'string[]' || infer.stringArray.length !== 2) {
    return null;
  }

  return infer.stringArray[1];
};

/**
 * If you are trying to figure out why the infer logic is here, see the below references.
 * @see ComparisonGroupMenu
 * @see determineDefaultValueForPrimaryFilter
 * @param primaryFilterValues
 */
export const getReferenceLevel = (
  primaryFilterValues: PrimaryFilterSelection[],
) => {
  return getStringValueFromPrimaryFilterValues({
    primaryFilterValues,
    primaryFilterKey: 'comparisonGroup',
  });
};

export const getDeepDivePrimaryFilterSelectionsFromRequest = ({
  request,
  getLevelByColumnValue,
  getPrivilegedLevelForDimension,
}: {
  request: Pick<BiasAnalysisRequest, 'inputs'>;
  getPrivilegedLevelForDimension: UseDimensionConfigPayload['getPrivilegedLevelForDimension'];
  getLevelByColumnValue: UseDimensionConfigPayload['getLevelByColumnValue'];
}): PrimaryFilterSelection[] => {
  const primaryFilterSelections: PrimaryFilterSelection[] = [];

  const dimension = request.inputs.extras.dimension;

  const referenceGroup = getPrivilegedLevelForDimension(dimension);

  const treatmentGroup = getLevelByColumnValue(
    dimension,
    request.inputs.extras.treatmentDimensionLevel ?? '',
  );

  if (treatmentGroup) {
    primaryFilterSelections.push({
      primaryFilterKey: 'employeeGroup',
      value: [dimension, treatmentGroup.columnValue],
    });
  }

  if (referenceGroup) {
    primaryFilterSelections.push({
      primaryFilterKey: 'comparisonGroup',
      value: referenceGroup.columnValue,
    });
  }

  return primaryFilterSelections;
};

export const getDeepDiveNumberOfSubjectComponents = ({
  metricDefinition,
  numberOfTreatmentGroupSubjects,
  numberOfReferenceGroupSubjects,
  treatmentGroupLevelDefinition,
  referenceGroupLevelDefinition,
  numeratorOfTreatmentGroupSubjects,
  numeratorOfReferenceGroupSubjects,
}: {
  metricDefinition: MyMetricDefinition | null;
  numberOfTreatmentGroupSubjects: number | null;
  numberOfReferenceGroupSubjects: number | null;
  treatmentGroupLevelDefinition: LevelDefinition | null;
  referenceGroupLevelDefinition: LevelDefinition | null;
  numeratorOfTreatmentGroupSubjects: number | null;
  numeratorOfReferenceGroupSubjects: number | null;
}): DeepDiveNumberOfSubjectComponent[] => {
  if (
    !metricDefinition ||
    !treatmentGroupLevelDefinition ||
    !referenceGroupLevelDefinition
  ) {
    return [];
  }

  const subjectName = metricDefinition.whiteLabeling.subjectPlural;
  return [
    {
      numberOfSubjects:
        numberOfTreatmentGroupSubjects !== null
          ? numberOfTreatmentGroupSubjects.toString()
          : 'N/A',
      numerator: numeratorOfTreatmentGroupSubjects?.toString() ?? null,
      descriptorText: `${treatmentGroupLevelDefinition.displayName} ${subjectName}`,
    },
    {
      numberOfSubjects:
        numberOfReferenceGroupSubjects !== null
          ? numberOfReferenceGroupSubjects.toString()
          : 'N/A',
      numerator: numeratorOfReferenceGroupSubjects?.toString() ?? null,
      descriptorText: `${referenceGroupLevelDefinition.displayName} ${subjectName}`,
    },
  ];
};
